import { Eye, RotateCcw, Copy, Download, Trash2 } from "lucide-react"
import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { UprdrBatch } from "@shared/types/uprdr-batch.types"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"

interface UprdrBatchSpecificHandlers {
  onViewDetails?: (batch: UprdrBatch) => void
  onRetry?: (batch: UprdrBatch) => void
  onExportBatch?: (batch: UprdrBatch) => void
  onDeleteBatch?: (batch: UprdrBatch) => void
}

export type UprdrBatchActionHandlers = BaseActionHandlers<UprdrBatch> & UprdrBatchSpecificHandlers

const createUprdrBatchBulkActions = (): BulkAction[] => [
  {
    key: "retry",
    label: "Retry",
    icon: <RotateCcw className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
  {
    key: "delete",
    label: "Delete",
    icon: <Trash2 className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
]

const createUprdrBatchRowActions = (handlers: UprdrBatchActionHandlers): TableAction<UprdrBatch>[] => [
  {
    key: "view-details",
    label: "View details",
    icon: <Eye className="h-4 w-4" />,
    onClick: (batch) => handlers.onViewDetails?.(batch),
  },
  {
    key: "retry",
    label: "Retry batch",
    icon: <RotateCcw className="h-4 w-4" />,
    onClick: (batch) => handlers.onRetry?.(batch),
    condition: (batch) => batch.status === "failed",
  },
  {
    key: "export",
    label: "Export batch",
    icon: <Download className="h-4 w-4" />,
    onClick: (batch) => handlers.onExportBatch?.(batch),
    condition: (batch) => batch.status === "completed",
  },
  {
    key: "delete",
    label: "Delete batch",
    icon: <Trash2 className="h-4 w-4" />,
    onClick: (batch) => handlers.onDeleteBatch?.(batch),
  },
  {
    key: "copy-id",
    label: "Copy batch ID",
    icon: <Copy className="h-4 w-4" />,
    onClick: (batch) => handlers.onCopyId?.(batch),
  },
]

export const getUprdrBatchActions = (handlers: UprdrBatchActionHandlers = {}) => {
  const baseActions = createBaseActions<UprdrBatch>('Uprdr Batch', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: false,
    showAddNote: false,
    showCopyId: false,
    showExport: true,
  })

  const specificActions = {
    bulkActions: createUprdrBatchBulkActions(),
    rowActions: createUprdrBatchRowActions(handlers),
  }

  return mergeActions(baseActions, specificActions)
}

export const getUprdrBatchBulkActions = (handlers: UprdrBatchActionHandlers = {}): BulkAction[] =>
  getUprdrBatchActions(handlers).bulkActions

export const getUprdrBatchRowActions = (_batchData: UprdrBatch, handlers: UprdrBatchActionHandlers = {}): TableAction<UprdrBatch>[] =>
  getUprdrBatchActions(handlers).rowActions
